import { useMemo, useState, type ReactElement } from 'react';
import {
  getRecipeTotalMinutes,
  MEAL_ORDER,
  mealTypeLabel,
  pilaBadgeClass,
  pilaLabel,
  useChangeMealPlanItem,
  useMealPlanWeek,
  useRecipe,
  type CatalogMealType,
  type MealPlan,
  type MealPlanItem,
  type MealType,
  type PilaLevel,
  type Recipe,
} from '@/lib/nutrition';
import { WeekNavigator } from '@/features/dashboard/WeekNavigator';
import { ChangeRecipeModal } from './ChangeRecipeModal';

/**
 * Vista "Plan semanal" del módulo Nutrición.
 *
 * Decisiones:
 * - Reutilizamos `WeekNavigator` del dashboard tal cual (acepta callbacks
 *   `onChange(weekStartISO)`), de modo que la navegación entre semanas se
 *   comporta igual aquí y allí.
 * - Layout: stack vertical en mobile (1 columna), 7 columnas a partir de `lg`
 *   — replica el patrón del Dashboard para mantener consistencia visual.
 * - Orden de comidas dentro del día: usamos `MEAL_ORDER`
 *   (breakfast → pre → lunch → snack → post → dinner) que refleja el flujo
 *   real del día.
 * - Macros del día: card al pie de cada columna con kcal y % vs target si
 *   `totalKcalEstimated` está disponible. NO usamos librería de gráficas;
 *   suma y comparativa simple en texto, idéntico patrón a `LoadChart` del
 *   dashboard (barra de progreso CSS).
 * - Empty state: si la query devuelve `null` (no hay plan para esa semana)
 *   mostramos un CTA grande que invita a abrir el tab "Generar plan". Por
 *   defecto el CTA es un texto (no un link cross-tab) porque el cambio de
 *   tab vive en el padre — exponemos `onRequestGenerate?` y si no se pasa,
 *   sólo mostramos texto.
 */
export function WeekMealPlanView({
  weekStartISO,
  onWeekChange,
  onRequestGenerate,
}: {
  weekStartISO: string;
  onWeekChange: (newWeekStartISO: string) => void;
  onRequestGenerate?: () => void;
}): ReactElement {
  const query = useMealPlanWeek(weekStartISO);

  const weekEndISO = useMemo(() => addDaysISO(weekStartISO, 6), [weekStartISO]);

  return (
    <section aria-label="Plan semanal de comidas" className="space-y-4">
      <WeekNavigator
        weekStartISO={weekStartISO}
        weekEndISO={weekEndISO}
        onChange={onWeekChange}
      />

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando plan…
        </p>
      )}

      {query.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo cargar el plan semanal. {query.error?.message ?? ''}
        </p>
      )}

      {query.data === null && !query.isLoading && (
        // `exactOptionalPropertyTypes` exige no pasar `undefined` explícito en
        // props opcionales — usamos spread condicional para omitir la prop si
        // el padre no la pasó.
        <EmptyPlanState {...(onRequestGenerate ? { onRequestGenerate } : {})} />
      )}

      {query.data && <WeekGrid plan={query.data} weekStartISO={weekStartISO} />}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyPlanState({
  onRequestGenerate,
}: {
  onRequestGenerate?: () => void;
}): ReactElement {
  return (
    <section
      aria-label="Sin plan semanal"
      className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm"
    >
      <p className="text-sm text-slate-700">
        Aún no hay plan de comidas para esta semana.
      </p>
      {onRequestGenerate && (
        <button
          type="button"
          onClick={onRequestGenerate}
          className="mt-4 inline-flex items-center gap-1 rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Generar plan semanal
        </button>
      )}
      {!onRequestGenerate && (
        <p className="mt-2 text-xs text-slate-500">
          Ve a la pestaña &quot;Generar plan&quot; para crear uno.
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Grid Lun-Dom
// ---------------------------------------------------------------------------

function WeekGrid({
  plan,
  weekStartISO,
}: {
  plan: MealPlan;
  weekStartISO: string;
}): ReactElement {
  // Generamos las 7 fechas (lunes…domingo) y agrupamos los items por dateISO.
  const days = useMemo(() => buildDayBuckets(plan.items, weekStartISO), [plan.items, weekStartISO]);

  const targetKcalPerDay = useMemo(() => {
    if (!plan.totalKcalEstimated) return null;
    return Math.round(plan.totalKcalEstimated / 7);
  }, [plan.totalKcalEstimated]);

  return (
    <section
      aria-label="Comidas de la semana"
      className="rounded-lg border border-slate-200 bg-slate-50 p-3 shadow-sm"
    >
      <ul role="list" className="grid grid-cols-1 gap-3 lg:grid-cols-7">
        {days.map((day) => (
          <DayColumn
            key={day.dateISO}
            dateISO={day.dateISO}
            items={day.items}
            targetKcal={targetKcalPerDay}
          />
        ))}
      </ul>
    </section>
  );
}

interface DayBucket {
  dateISO: string;
  items: MealPlanItem[];
}

function buildDayBuckets(items: MealPlanItem[], weekStartISO: string): DayBucket[] {
  const map = new Map<string, MealPlanItem[]>();
  for (let i = 0; i < 7; i++) {
    map.set(dateOnly(addDaysISO(weekStartISO, i)), []);
  }
  for (const it of items) {
    const key = dateOnly(it.dateISO);
    const list = map.get(key);
    if (list) list.push(it);
  }
  // Orden por meal_type según MEAL_ORDER.
  const orderIndex = (t: MealType): number => MEAL_ORDER.indexOf(t);
  const out: DayBucket[] = [];
  for (const [dateISO, list] of map) {
    list.sort((a, b) => orderIndex(a.mealType) - orderIndex(b.mealType));
    out.push({ dateISO, items: list });
  }
  // Ordenar buckets por fecha cronológica (map preserva inserción, pero por
  // robustez si en el futuro se reusara).
  out.sort((a, b) => a.dateISO.localeCompare(b.dateISO));
  return out;
}

function DayColumn({
  dateISO,
  items,
  targetKcal,
}: {
  dateISO: string;
  items: MealPlanItem[];
  targetKcal: number | null;
}): ReactElement {
  const totals = useMemo(() => sumMacros(items), [items]);
  const pct =
    targetKcal && targetKcal > 0
      ? Math.min(200, Math.round((totals.kcal / targetKcal) * 100))
      : null;

  return (
    <li className="flex flex-col rounded-md border border-slate-200 bg-white p-3 shadow-sm">
      <header className="mb-2 flex items-baseline justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
          {dayLongLabel(dateISO)}
        </h3>
        <span className="text-xs text-slate-500">{dayNumber(dateISO)}</span>
      </header>

      <div className="flex flex-1 flex-col gap-2">
        {items.length === 0 ? (
          <p className="rounded-md border border-dashed border-slate-200 px-2 py-3 text-center text-xs text-slate-400">
            —
          </p>
        ) : (
          items.map((it) => <MealCard key={it.id} item={it} />)
        )}
      </div>

      <footer className="mt-3 border-t border-slate-200 pt-2 text-xs text-slate-600">
        <p className="font-medium text-slate-900">
          {totals.kcal} kcal
          {pct !== null && (
            <span
              className={`ml-2 text-xs ${
                pct >= 110
                  ? 'text-amber-700'
                  : pct >= 90
                    ? 'text-emerald-700'
                    : 'text-slate-500'
              }`}
            >
              {pct}% objetivo
            </span>
          )}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          P {totals.protein_g}g · C {totals.carbs_g}g · G {totals.fat_g}g
        </p>
        {pct !== null && (
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={200}
            aria-valuenow={pct}
            aria-label={`Progreso del objetivo de kcal: ${pct}%`}
            className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200"
          >
            <div
              className={`h-full ${pct >= 110 ? 'bg-amber-500' : 'bg-teal-600'}`}
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        )}
      </footer>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Card por comida
// ---------------------------------------------------------------------------

function MealCard({ item }: { item: MealPlanItem }): ReactElement {
  // M2.34.A — Si el backend embebió la `recipe` (caso normal cuando
  // recipeId != null), la usamos para mostrar el tiempo total como badge
  // y evitamos un fetch extra. Si no hay recipe embebida pero sí recipeId,
  // `RecipeLink` hace un fetch lazy con `useRecipe(id)` para mostrar al
  // menos el título cuando llegue.
  const embeddedRecipe = item.recipe ?? null;
  // M2.36 — modal "Cambiar receta" sólo disponible si el meal tiene recipeId
  // (planes pre-M2.34 sin receta vinculada mantienen el comportamiento legacy).
  const [showChange, setShowChange] = useState(false);
  const changeMutation = useChangeMealPlanItem();
  const hasRecipe = item.recipeId !== null && item.recipeId !== undefined;
  // M2.36 — pila del meal (viene del backend en planes nuevos; fallback null).
  const pila = (item.pilaLevel ?? null) as PilaLevel | null;

  function handleSelect(recipeId: number | string, servings: number) {
    // Sólo soportamos ids numéricos en el endpoint actual (templates en el
    // catálogo pueden venir como string si se usan slugs; el backend M2.36
    // los acepta vía el wrapper).
    changeMutation.mutate(
      {
        itemId: item.id,
        body: { recipeId, servings },
      },
      {
        onSuccess: () => setShowChange(false),
      },
    );
  }

  return (
    <article className={`rounded-md border ${mealBorderClass(item.mealType)} bg-slate-50/60 p-2`}>
      <header className="flex flex-wrap items-baseline justify-between gap-1">
        <span className={`text-xs font-medium uppercase tracking-wide ${mealLabelClass(item.mealType)}`}>
          {mealTypeLabel(item.mealType)}
        </span>
        <div className="flex items-center gap-1">
          {pila !== null && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${pilaBadgeClass(pila)}`}
              title={`Día clasificado como ${pilaLabel(pila)}`}
            >
              {pilaLabel(pila)}
            </span>
          )}
          {item.kcal !== null && item.kcal !== undefined && (
            <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">
              {item.kcal} kcal
            </span>
          )}
        </div>
      </header>
      <p className="mt-1 text-sm font-medium text-slate-900">{item.title}</p>
      {embeddedRecipe && <RecipeTimeBadge recipe={embeddedRecipe} />}
      {item.description && (
        <p className="mt-1 text-xs text-slate-600">{item.description}</p>
      )}
      <MacroLine item={item} />
      {hasRecipe && item.recipeId !== null && item.recipeId !== undefined && (
        <RecipeLink recipeId={item.recipeId} embeddedRecipe={embeddedRecipe} />
      )}
      {hasRecipe && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => setShowChange(true)}
            disabled={changeMutation.isPending}
            className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:bg-slate-200 focus:outline-none focus:ring-1 focus:ring-teal-600"
          >
            Cambiar receta
          </button>
          <button
            type="button"
            disabled
            title="Próximamente"
            aria-disabled="true"
            className="cursor-not-allowed rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-400"
          >
            Ajustar servings
          </button>
        </div>
      )}
      {changeMutation.isError && (
        <p
          role="alert"
          className="mt-1 rounded bg-rose-50 px-1.5 py-0.5 text-[11px] text-rose-700"
        >
          {changeMutation.error?.message ?? 'No se pudo cambiar la receta.'}
        </p>
      )}
      {item.notes && <p className="mt-1 text-xs italic text-slate-500">{item.notes}</p>}

      {showChange && (
        <ChangeRecipeModal
          currentRecipeId={item.recipeId ?? null}
          pilaLevel={pila}
          mealType={toCatalogMealType(item.mealType)}
          onClose={() => setShowChange(false)}
          onSelect={handleSelect}
        />
      )}
    </article>
  );
}

/**
 * Mapea el `MealType` interno (alineado con la tabla `meal_plan_items`) al
 * superset del catálogo (`CatalogMealType`). Coinciden 1:1, así que es un cast
 * con narrowing — lo dejamos explícito para que TypeScript valide si el día
 * de mañana añadimos un tipo al catálogo que NO existe en el plan.
 */
function toCatalogMealType(t: MealType): CatalogMealType {
  return t as CatalogMealType;
}

/**
 * M2.34.A — Badge con tiempo total de prep+cook ("⏱ 30 min"). Solo se renderiza
 * si la receta declara prep_min o cook_min > 0 (recetas pre-M2.34 sin tiempos
 * no muestran nada).
 */
function RecipeTimeBadge({ recipe }: { recipe: Recipe }): ReactElement | null {
  const total = getRecipeTotalMinutes(recipe);
  if (total === null || total <= 0) return null;
  return (
    <p className="mt-1">
      <span
        className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200"
        aria-label={`Tiempo total de preparación: ${total} minutos`}
        title={`Prep ${recipe.prep_min ?? 0} min · Cocción ${recipe.cook_min ?? 0} min`}
      >
        <span aria-hidden="true">⏱</span> {total} min
      </span>
    </p>
  );
}

function MacroLine({ item }: { item: MealPlanItem }): ReactElement | null {
  const hasAny =
    (item.protein_g ?? null) !== null ||
    (item.carbs_g ?? null) !== null ||
    (item.fat_g ?? null) !== null;
  if (!hasAny) return null;
  return (
    <p className="mt-1 text-xs text-slate-500">
      P {item.protein_g ?? 0}g · C {item.carbs_g ?? 0}g · G {item.fat_g ?? 0}g
    </p>
  );
}

/**
 * Link a la receta asociada. Como no tenemos rutas profundas para recetas
 * (viven dentro del tab "Recetas"), exponemos un link inline que navega a la
 * pestaña `?tab=recetas&recipeId={id}` — RecipesView lo lee y filtra/expande
 * automáticamente. Si el backend ya embebió la `recipe` en el item (M2.34.A),
 * la usamos para el título y NO disparamos un fetch extra.
 */
function RecipeLink({
  recipeId,
  embeddedRecipe,
}: {
  recipeId: number;
  embeddedRecipe: Recipe | null;
}): ReactElement {
  // Solo hacemos fetch si no hay receta embebida (compat con planes
  // pre-M2.34.A persistidos sin recipe_id, donde el backend devuelve
  // `recipe: null` pero recipeId apunta a una receta válida — caso raro).
  const query = useRecipe(embeddedRecipe ? null : recipeId);
  const title = embeddedRecipe?.title ?? query.data?.title;
  return (
    <p className="mt-1 text-xs">
      <a
        href={`/nutricion?tab=recetas&recipeId=${recipeId}`}
        className="font-medium text-teal-700 hover:text-teal-800 hover:underline focus:outline-none focus:underline"
      >
        Ver receta{title ? `: ${title}` : ''} →
      </a>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Macros helpers
// ---------------------------------------------------------------------------

interface MacrosTotal {
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

function sumMacros(items: MealPlanItem[]): MacrosTotal {
  return items.reduce<MacrosTotal>(
    (acc, it) => ({
      kcal: acc.kcal + (it.kcal ?? 0),
      protein_g: acc.protein_g + (it.protein_g ?? 0),
      carbs_g: acc.carbs_g + (it.carbs_g ?? 0),
      fat_g: acc.fat_g + (it.fat_g ?? 0),
    }),
    { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
  );
}

// ---------------------------------------------------------------------------
// Style helpers — colores por tipo de comida
// ---------------------------------------------------------------------------

function mealBorderClass(t: MealType): string {
  switch (t) {
    case 'breakfast':
      return 'border-amber-200';
    case 'pre_workout':
      return 'border-sky-200';
    case 'lunch':
      return 'border-emerald-200';
    case 'snack':
      return 'border-slate-200';
    case 'post_workout':
      return 'border-purple-200';
    case 'dinner':
      return 'border-indigo-200';
  }
}

function mealLabelClass(t: MealType): string {
  switch (t) {
    case 'breakfast':
      return 'text-amber-800';
    case 'pre_workout':
      return 'text-sky-800';
    case 'lunch':
      return 'text-emerald-800';
    case 'snack':
      return 'text-slate-700';
    case 'post_workout':
      return 'text-purple-800';
    case 'dinner':
      return 'text-indigo-800';
  }
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function addDaysISO(iso: string, days: number): string {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
}

function dateOnly(iso: string): string {
  // YYYY-MM-DD desde una cadena ISO; usamos UTC para consistencia con el backend.
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso.slice(0, 10);
  }
}

function dayLongLabel(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: 'UTC',
      weekday: 'long',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function dayNumber(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: 'UTC',
      day: '2-digit',
      month: 'short',
    }).format(new Date(iso));
  } catch {
    return '';
  }
}
